import { useCallback, useReducer } from "react";

import { fetchBoard, fetchBoards, fetchChampions, fetchTracks } from "../../api/hallOfFame";
import { readCache, writeCache } from "../../utils/cache";
import { SIMULATORS } from "../../utils/simulators";

// Bumped to v5 when soft-deleted drivers were filtered out server-side: the old key would
// otherwise keep serving boards that still list them for up to three hours.
const CACHE_KEY = "hallOfFameCache_v5";

// Every dataset starts at its own "not loaded yet" value — an absent boardsByGroup entry, a null
// tracks/champions — and that is what the page reads to decide between a skeleton and the real
// thing. A single shared `loading` flag could not work here: four loaders race, and the first one
// home would clear it for the other three.
function buildInitialState(simulatorId) {
  return {
    simulatorId: simulatorId || SIMULATORS[0].id,
    boardsByGroup: {},
    tracks: null,
    champions: null,
    // Keyed the same way the loaders are ("boards:DRIVERS", "tracks", "champions"), so a failure
    // takes down only the section that asked for it — the others keep their skeletons and land.
    errors: {},
  };
}

function reducer(state, action) {
  switch (action.type) {
    // Switching simulators swaps every dataset, so the old one is dropped rather than left on
    // screen as stale numbers while the new one loads.
    case "SET_SIMULATOR":
      return buildInitialState(action.simulatorId);
    case "BOARDS_LOADED":
      return {
        ...state,
        boardsByGroup: { ...state.boardsByGroup, [action.group]: action.boards },
      };
    case "TRACKS_LOADED":
      return { ...state, tracks: action.tracks };
    case "CHAMPIONS_LOADED":
      return { ...state, champions: action.champions };
    case "LOAD_STARTED": {
      if (!state.errors[action.key]) return state;
      const errors = { ...state.errors };
      delete errors[action.key];
      return { ...state, errors };
    }
    case "LOAD_FAILED":
      return { ...state, errors: { ...state.errors, [action.key]: true } };
    default:
      return state;
  }
}

async function cached(itemKey, fetcher) {
  const hit = readCache({ cacheKey: CACHE_KEY, itemKey });
  if (hit) return hit;
  const value = await fetcher();
  writeCache({ cacheKey: CACHE_KEY, itemKey, value });
  return value;
}

export function useHallOfFameData(initialSimulatorId) {
  const [state, dispatch] = useReducer(reducer, initialSimulatorId, buildInitialState);

  const loadBoards = useCallback(async (group, simulatorId) => {
    const key = `boards:${group}`;
    dispatch({ type: "LOAD_STARTED", key });
    try {
      const boards = await cached(`boards:${group}:${simulatorId}`, () => fetchBoards(group, simulatorId));
      dispatch({ type: "BOARDS_LOADED", group, boards });
      return boards;
    } catch (error) {
      dispatch({ type: "LOAD_FAILED", key });
      return undefined;
    }
  }, []);

  const loadFullBoard = useCallback(
    (kind, simulatorId) => cached(`board:${kind}:${simulatorId}`, () => fetchBoard(kind, simulatorId)),
    [],
  );

  const loadTracks = useCallback(async (simulatorId) => {
    dispatch({ type: "LOAD_STARTED", key: "tracks" });
    try {
      const tracks = await cached(`tracks:${simulatorId}`, () => fetchTracks(simulatorId));
      dispatch({ type: "TRACKS_LOADED", tracks });
      return tracks;
    } catch (error) {
      dispatch({ type: "LOAD_FAILED", key: "tracks" });
      return undefined;
    }
  }, []);

  const loadChampions = useCallback(async (simulatorId) => {
    dispatch({ type: "LOAD_STARTED", key: "champions" });
    try {
      const champions = await cached(`champions:${simulatorId}`, () => fetchChampions(simulatorId));
      dispatch({ type: "CHAMPIONS_LOADED", champions });
      return champions;
    } catch (error) {
      dispatch({ type: "LOAD_FAILED", key: "champions" });
      return undefined;
    }
  }, []);

  const setSimulator = useCallback((simulatorId) => {
    dispatch({ type: "SET_SIMULATOR", simulatorId });
  }, []);

  return { state, setSimulator, loadBoards, loadFullBoard, loadTracks, loadChampions };
}
